import type { CSSProperties } from 'react'
import { useGovernance } from './GovernanceProvider'
import { SAVED_STATUS_META } from './governanceUi'
import type { GovToast } from './governanceModel'

const TOAST_VAR: Record<GovToast['kind'], string> = {
  success: 'var(--govv-verified)',
  attention: 'var(--govv-attention)',
  denied: 'var(--govv-denied)',
  info: 'var(--govv-structure)',
}

const wrapStyle: CSSProperties = {
  position: 'fixed', right: 20, bottom: 20, zIndex: 80,
  display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380,
}

/**
 * GovToaster — the synchronized-mutation feedback rail. Each governed action
 * produces a toast that names the shared correlation id and its honest
 * saved-status, so an operator sees the cross-module propagation and never
 * mistakes a local-session write for a durable one.
 */
export default function GovToaster() {
  const gov = useGovernance()
  const toasts = gov.state.toasts
  if (toasts.length === 0) return null
  return (
    <div style={wrapStyle} aria-live="polite" aria-label="Governance activity">
      {toasts.slice(-4).map((t) => {
        const saved = SAVED_STATUS_META[t.savedStatus]
        return (
          <div key={t.id} role="status" style={{
            border: `1px solid ${TOAST_VAR[t.kind]}`, borderLeftWidth: 3,
            borderRadius: 'var(--sc-radius-md)', background: 'var(--govv-canvas)',
            padding: '10px 12px', boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--govv-ink)' }}>{t.message}</span>
              <button type="button" aria-label="Dismiss" className="olv-gov-btn"
                onClick={() => gov.run({ type: 'dismiss_toast', id: t.id })}
                style={{ padding: '0 6px', lineHeight: 1.6, background: 'transparent', border: 'none', color: 'var(--govv-ink-3)' }}>
                ×
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <span className="olv-gov-pill" style={{ color: saved.varName }} title={saved.tip}>{saved.label}</span>
              {t.correlationId && <span className="olv-gov-pill" style={{ color: 'var(--govv-ink-3)' }}>{t.correlationId}</span>}
            </div>
          </div>
        )
      })}
    </div>
  )
}
